import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { AbsenceStatus } from '~/data/absences';
import { AbsenceEntry, AbsenceType } from '~/data/absences/types';
import { useEmployeeAbsenceData } from '~/data/absences/useEmployeeAbsenceData';
import { useApplicationConfig } from '~/data/applicationConfig';
import { useCompanyData } from '~/data/company/useCompanyData';
import { Enum } from '~/data/enums';
import { useLegalEntity } from '~/data/legalEntity/useLegalEntity';
import { useHasChanged } from '~/hooks/useHasChanged';
import { AbsencesTabContent_OnUpdateLegacy } from '~/library/AbsencesTabContent/component';
import {
  AvailableBalances,
  BalancePool,
  BalancePoolCategory,
  useVacationBalances,
} from '~/scenes/MyWorkspace/components/Widgets/components/MyDataWidget/components/Content/useVacationBalances';
import { useGetPublicProfile } from '~/services/Http/hooks';
import {
  convertDateToMidnight,
  isDateWithinRange,
  isSameDay,
} from '~/utils/helpers/date';
import {
  filterActiveEnumsByCountryAndOrgUnits,
  sortEnumsBySeq,
} from '~/utils/helpers/enum';

type PropsType = {
  employeeId: number;
  absenceId?: string;
  absenceType?: AbsenceType;
} & AbsencesTabContent_OnUpdateLegacy;

export type EditOverlappingAbsenceProps = {
  enabled: boolean;
  absenceTypes: string[];
  isOtherAbsenceDate: (date: Date) => boolean;
  isOverlapAllowedOtherAbsenceDate: (
    date: Date,
    reasonCode?: string,
  ) => boolean;
  hasAbsenceOverlap: (
    startDate: Date | null,
    endDate: Date | null,
    reasonCode: string,
  ) => boolean;
};

export type EditAbsenceDialogFormProps = {
  absenceEntries: AbsenceEntry[];
  reasonOptions: Enum[];
  selectedAbsence: AbsenceEntry | undefined;
  selectedAbsenceType: AbsenceType;
  employee: {
    legalEntityId?: number;
  };
  companyVacationTypes: {
    vacationTypes: string[];
    extraVacationTypes: string[];
    yearlyVacationTypes: string[];
    vacationBankTypes: string[];
  };
  isHoursFieldEnabled: (
    startDate?: Date | null,
    endDate?: Date | null,
  ) => boolean;
  permissions: {
    canEditDays: boolean;
  };
  overlappingAbsence: EditOverlappingAbsenceProps;
  sickLeaves: {
    getSickLeaveTypeDayLimit: (reasonCode: string) => number;
    isSickLeave: (reasonCode: string) => boolean;
    isSickLeaveCountSpent: (reasonCode: string, startDate: Date) => boolean;
    getNextSickLeaveCountResetDate: (startDate: Date) => Date;
  };
  balances: {
    availableBalances: AvailableBalances;
    getAvailableBalance: (
      absenceReason: Enum,
      year?: Enum,
      yearlySum?: boolean,
    ) => BalancePool | undefined;
    isBalancePoolReasonMatch: (
      pool: BalancePool,
      reasonCode?: string,
    ) => boolean;
  };
  isLoadingData: boolean;
  failedToLoadData: boolean;
  refreshAbsencesData: () => Promise<void>;
};

type UseEditAbsenceDialogDataOutput = {
  selectedAbsenceType: AbsenceType;
  categoryTabs: {
    enabled: boolean;
    selected: AbsenceType;
    onTabChange: (absenceType: AbsenceType) => void;
  };
  formProps: EditAbsenceDialogFormProps;
  isLoading: boolean;
  failedToLoad: boolean;
};

export const useEditAbsenceDialogData = (
  props: PropsType,
): UseEditAbsenceDialogDataOutput => {
  const {
    employeeId,
    absenceId,
    absenceType: defaultAbsenceType,
    onUpdateLegacy,
  } = props;

  const loggedInEmployeeId = useLoggedInEmployeeId();
  const isOwnAbsence = loggedInEmployeeId === employeeId;

  const isBalancePoolReasonMatch = useCallback(
    (pool: BalancePool, reasonCode?: string): boolean =>
      !!(reasonCode && pool.reasons?.some((r) => r.code === reasonCode)),
    [],
  );

  // Load Configs & Enums
  const {
    applicationConfig,
    isLoading: isLoadingApplicationConfig,
    failedToLoad: failedToLoadApplicationConfig,
  } = useApplicationConfig();
  const { companyData } = useCompanyData();

  const {
    companyVacationTypes,
    overlappingAbsencesAllowed,
    overlappingAbsencesTypes,
    absenceHoursEnabled,
    sickLeaves,
  } = useMemo(() => {
    const leaveTypes = companyData?.config?.fComLeaveTypes ?? {
      fComVacationLeaveTypes: [],
      fComYearlyVacationBalanceTypes: [],
      fComVacationBankLeaveTypes1: [],
      fComVacationBankLeaveTypes2: [],
      fComVacationBankLeaveTypes3: [],
      fComVacationLeaveExtraTypes: [],
      fComOverlappingAbsencesTypes: [],
      fComSickLeaveTypes: [],
      fComSickLeaveCountLimit: { limit: 0, reset: '' },
      fComSickLeaveTypesDayLimit: [],
    };

    const {
      fComVacationLeaveTypes,
      fComYearlyVacationBalanceTypes,
      fComVacationBankLeaveTypes1,
      fComVacationBankLeaveTypes2,
      fComVacationBankLeaveTypes3,
      fComVacationLeaveExtraTypes,
      fComOverlappingAbsencesTypes,
      fComSickLeaveTypes,
      fComSickLeaveCountLimit,
      fComSickLeaveTypesDayLimit,
    } = leaveTypes;

    const availableTypes = (
      typesList: string[],
      configValue?: boolean,
    ): string[] => {
      return !!configValue && typesList.length > 0 ? typesList : [];
    };

    const vacationBanksEnabled =
      applicationConfig?.fComConfig?.absConfig?.fComEnableVacationBank || false;
    const yearlyVacationBalancesEnabled =
      applicationConfig?.fComConfig?.absConfig
        ?.fComEnableYearlyVacationBalance || false;
    const overlappingAbsencesAllowed =
      (applicationConfig?.fComConfig?.absConfig
        ?.fComEnableOverlappingAbsences ||
        false) &&
      fComOverlappingAbsencesTypes.length > 0;

    const absenceHoursEnabled =
      applicationConfig?.fComConfig?.absConfig?.fComEnableAbsenceHours || false;

    const resolveLastResetDate = (resetDateString: string): Date => {
      const resetDate = moment(resetDateString, 'DD.MM');

      return (
        resetDate.isBefore(moment(), 'day')
          ? resetDate
          : resetDate.subtract(1, 'year')
      ).toDate();
    };

    return {
      companyVacationTypes: {
        vacationTypes: fComVacationLeaveTypes,
        extraVacationTypes: availableTypes(
          fComVacationLeaveExtraTypes,
          !vacationBanksEnabled,
        ),
        yearlyVacationTypes: availableTypes(
          fComYearlyVacationBalanceTypes,
          yearlyVacationBalancesEnabled,
        ),
        vacationBankTypes: availableTypes(
          [
            ...fComVacationBankLeaveTypes1,
            ...fComVacationBankLeaveTypes2,
            ...fComVacationBankLeaveTypes3,
          ],
          vacationBanksEnabled,
        ),
      },
      overlappingAbsencesTypes: availableTypes(
        fComOverlappingAbsencesTypes,
        overlappingAbsencesAllowed,
      ),
      overlappingAbsencesAllowed,
      absenceHoursEnabled,
      sickLeaves: {
        types: fComSickLeaveTypes,
        count: {
          ...fComSickLeaveCountLimit,
          lastResetDate: resolveLastResetDate(fComSickLeaveCountLimit.reset),
        },
        typeDayLimits: fComSickLeaveTypesDayLimit,
      },
    };
  }, [companyData, applicationConfig]);

  // Load Absence Data & Process Balances
  const {
    absencesData,
    absenceEntries,
    absenceReasons,
    isLoading: isLoadingAbsenceData,
    mutate: mutateAbsences,
  } = useEmployeeAbsenceData({ employeeId });

  const refreshAbsencesData = useCallback((): Promise<void> => {
    if (onUpdateLegacy) {
      onUpdateLegacy(['absence-edit']);
    }

    return !!mutateAbsences
      ? mutateAbsences().then(() => {})
      : Promise.resolve();
  }, [mutateAbsences, onUpdateLegacy]);

  const { availableBalances } = useVacationBalances({ absence: absencesData });

  const getAvailableBalance = useCallback(
    (absenceReason: Enum, year?: Enum, yearlySum?: boolean) => {
      const {
        yearlyVacationTypes,
        vacationBankTypes,
        vacationTypes,
        extraVacationTypes,
      } = companyVacationTypes;
      const { balancePools } = availableBalances;

      const reasonMatch = (pool: BalancePool) =>
        isBalancePoolReasonMatch(pool, absenceReason.code);

      // Yearly Vacation Balance
      if (yearlyVacationTypes.includes(absenceReason.code)) {
        const yearlyPools = balancePools.filter(
          (p) => p.category === BalancePoolCategory.YearlyVacationBalance,
        );

        if (!!year) {
          return yearlyPools.find(
            (p) => p.name === year.code && reasonMatch(p),
          );
        }
        if (!!yearlySum) {
          return yearlyPools
            .filter((p) => reasonMatch(p))
            .reduce(
              (
                acc: BalancePool | undefined,
                pool: BalancePool,
              ): BalancePool | undefined => {
                return !!acc
                  ? {
                      ...acc,
                      balance: acc.balance + pool.balance,
                    }
                  : pool;
              },
              undefined,
            );
        }
        return undefined;
      }

      // Vacation Bank Balance
      if (vacationBankTypes.includes(absenceReason.code)) {
        return balancePools
          .filter((p) => p.category === BalancePoolCategory.VacationBankBalance)
          .find(reasonMatch);
      }

      // Extra Vacation Balance
      if (extraVacationTypes.includes(absenceReason.code)) {
        return balancePools.find(
          (p) => p.category === BalancePoolCategory.ExtraVacationBalance,
        );
      }

      // Vacation Balance (Default)
      if (vacationTypes.includes(absenceReason.code)) {
        return balancePools.find(
          (p) => p.category === BalancePoolCategory.VacationBalance,
        );
      }

      return undefined;
    },
    [companyVacationTypes, availableBalances, isBalancePoolReasonMatch],
  );

  // Load Profile & Legal Entity Data
  const {
    data: profile,
    failedToLoad: failedToLoadProfile,
    isLoading: isLoadingProfile,
  } = useGetPublicProfile({ employeeId: employeeId.toString() });

  const { legalEntityId, orgUnitId } = useMemo(() => {
    const isProfileInSync = profile?.id === employeeId;

    return {
      isProfileInSync,
      legalEntityId: profile?.legalEntity.id,
      orgUnitId: profile?.unit.id,
    };
  }, [profile, employeeId]);

  const {
    legalEntity,
    isLoading: isLoadingLegalEntity,
    failedToLoad: failedToLoadLegalEntity,
  } = useLegalEntity(legalEntityId);

  const isLoading =
    isLoadingAbsenceData ||
    isLoadingProfile ||
    isLoadingLegalEntity ||
    isLoadingApplicationConfig;
  const failedToLoad =
    failedToLoadProfile ||
    failedToLoadLegalEntity ||
    failedToLoadApplicationConfig;

  // Process Absence Entries & Resolve Absence Reasons
  const { availableAbsenceReasons, selectedAbsence, otherAbsenceEntries } =
    useMemo(() => {
      const selectedAbsence = !!absenceId
        ? absenceEntries.find((a) => a.uid === absenceId)
        : undefined;

      const getRelevantReasons = (enums: Enum[]): Enum[] =>
        sortEnumsBySeq(
          filterActiveEnumsByCountryAndOrgUnits(
            enums,
            legalEntity?.fLegalEntityCountry,
            orgUnitId,
          ),
        );

      const availableAbsenceReasons = {
        short: getRelevantReasons(absenceReasons.short),
        long: getRelevantReasons(absenceReasons.long),
      };

      const otherAbsenceEntries = absenceEntries.filter(
        (entry) =>
          entry.id !== selectedAbsence?.id &&
          ![AbsenceStatus.REJECTED].includes(entry.status),
      );

      return {
        availableAbsenceReasons,
        selectedAbsence,
        otherAbsenceEntries,
      };
    }, [absenceEntries, absenceReasons, absenceId, legalEntity, orgUnitId]);

  const getReasonOptions = useCallback(
    (absenceType: AbsenceType) => {
      return absenceType === AbsenceType.SHORT
        ? availableAbsenceReasons.short
        : availableAbsenceReasons.long;
    },
    [availableAbsenceReasons],
  );

  const [selectedAbsenceType, setSelectedAbsenceType] = useState<AbsenceType>(
    selectedAbsence?.type ?? defaultAbsenceType ?? AbsenceType.SHORT,
  );
  const [reasonOptions, setReasonOptions] = useState<Enum[]>(
    getReasonOptions(selectedAbsenceType),
  );

  const handleAbsenceTypeSelect = useCallback(
    (absenceType: AbsenceType) => {
      setSelectedAbsenceType(absenceType);
      setReasonOptions(getReasonOptions(absenceType));
    },
    [setSelectedAbsenceType, setReasonOptions, getReasonOptions],
  );

  const shouldResetSelectedAbsenceType = useHasChanged(
    `${absenceId}_${defaultAbsenceType}`,
  );
  useEffect(() => {
    if (shouldResetSelectedAbsenceType) {
      handleAbsenceTypeSelect(
        selectedAbsence?.type ?? defaultAbsenceType ?? AbsenceType.SHORT,
      );
    }
  }, [
    shouldResetSelectedAbsenceType,
    handleAbsenceTypeSelect,
    selectedAbsence,
    defaultAbsenceType,
  ]);

  const hasAvailableAbsenceReasonsChanged = useHasChanged(
    availableAbsenceReasons,
  );
  useEffect(() => {
    if (hasAvailableAbsenceReasonsChanged) {
      setReasonOptions(getReasonOptions(selectedAbsenceType));
    }
  }, [
    hasAvailableAbsenceReasonsChanged,
    setReasonOptions,
    getReasonOptions,
    selectedAbsenceType,
  ]);

  const isDateInAbsenceEntries = useCallback(
    (entries: AbsenceEntry[], date: Date) => {
      return !!entries?.find((ab) => {
        if (!ab.endDate || !ab.startDate) {
          return false;
        }

        return isDateWithinRange(date, ab.startDate, ab.endDate);
      });
    },
    [],
  );

  const isOtherAbsenceDate = useCallback(
    (date: Date) => {
      return isDateInAbsenceEntries(otherAbsenceEntries, date);
    },
    [otherAbsenceEntries, isDateInAbsenceEntries],
  );

  const isOverlapAllowedOtherAbsenceDate = useCallback(
    (date: Date, reasonCode?: string) => {
      const entries = overlappingAbsencesAllowed
        ? otherAbsenceEntries.filter((entry) => {
            const code = entry.reason?.code;

            return (
              !!code &&
              overlappingAbsencesTypes.includes(code) &&
              code !== reasonCode
            );
          })
        : [];

      return entries.length > 0 ? isDateInAbsenceEntries(entries, date) : false;
    },
    [
      otherAbsenceEntries,
      overlappingAbsencesAllowed,
      overlappingAbsencesTypes,
      isDateInAbsenceEntries,
    ],
  );

  const hasAbsenceOverlap = useCallback(
    (startDate: Date | null, endDate: Date | null, reasonCode: string) => {
      const filterAbsencesForOverlapCheck = (reasonCode: string) => {
        if (
          overlappingAbsencesAllowed &&
          overlappingAbsencesTypes.includes(reasonCode)
        ) {
          return otherAbsenceEntries.filter((entry) => {
            const code = entry.reason?.code;

            return (
              !!code &&
              (!overlappingAbsencesTypes.includes(code) || code === reasonCode)
            );
          });
        }

        return otherAbsenceEntries;
      };

      const findAbsenceOverlap = (entries: AbsenceEntry[]) => {
        const editedAbsence = {
          startDate: convertDateToMidnight(startDate || ''),
          endDate: convertDateToMidnight(endDate || ''),
        };

        const foundOverlappingAbsence = entries?.find((ab) => {
          if (!ab.endDate || !ab.startDate) {
            return false;
          }

          const otherAbsence = {
            startDate: convertDateToMidnight(ab.startDate),
            endDate: convertDateToMidnight(ab.endDate),
          };

          // New start or end date within old range,
          // or old range is within new range
          return (
            isDateWithinRange(
              editedAbsence.startDate,
              otherAbsence.startDate,
              otherAbsence.endDate,
            ) ||
            isDateWithinRange(
              editedAbsence.endDate,
              otherAbsence.startDate,
              otherAbsence.endDate,
            ) ||
            isDateWithinRange(
              otherAbsence.startDate,
              editedAbsence.startDate,
              editedAbsence.endDate,
            )
          );
        });

        return !!foundOverlappingAbsence;
      };

      return findAbsenceOverlap(filterAbsencesForOverlapCheck(reasonCode));
    },
    [overlappingAbsencesAllowed, overlappingAbsencesTypes, otherAbsenceEntries],
  );

  const isSickLeave = useCallback(
    (reasonCode: string) => {
      const { types } = sickLeaves;

      return types.includes(reasonCode);
    },
    [sickLeaves],
  );

  const getSickLeaveTypeDayLimit = useCallback(
    (reasonCode: string) => {
      const { types, typeDayLimits } = sickLeaves;

      if (types.includes(reasonCode) && typeDayLimits.length > 0) {
        const limit = typeDayLimits.find((l) => l.type === reasonCode)?.limit;

        return !!limit && limit > 0 ? limit : 0;
      }

      return 0;
    },
    [sickLeaves],
  );

  const resolveSickLeaveCountRange = useCallback(
    (date: Date, lastReset: Date): { startDate: Date; endDate: Date } => {
      const getRange = (lastResetDate: Date) => {
        return {
          startDate: lastResetDate,
          endDate: moment(lastResetDate)
            .add(1, 'year')
            .subtract(1, 'day')
            .toDate(),
        };
      };

      if (moment(date).isSameOrAfter(lastReset, 'day')) {
        return getRange(lastReset);
      }

      const dateYearResetDate = moment(lastReset).year(moment(date).year());

      return getRange(
        moment(date).isSameOrAfter(dateYearResetDate, 'day')
          ? dateYearResetDate.toDate()
          : dateYearResetDate.subtract(1, 'year').toDate(),
      );
    },
    [],
  );

  const getNextSickLeaveCountResetDate = useCallback(
    (startDate: Date): Date => {
      const {
        count: { lastResetDate },
      } = sickLeaves;

      const countRange = resolveSickLeaveCountRange(startDate, lastResetDate);

      return moment(countRange.endDate).add(1, 'day').toDate();
    },
    [sickLeaves, resolveSickLeaveCountRange],
  );

  const isSickLeaveCountSpent = useCallback(
    (reasonCode: string, startDate: Date): boolean => {
      const {
        count: { lastResetDate, limit },
        types,
      } = sickLeaves;

      if (!types.includes(reasonCode) || limit === 0) {
        return false;
      }

      const countRange = resolveSickLeaveCountRange(startDate, lastResetDate);

      const sickLeaveEntries = otherAbsenceEntries.filter(
        (a) =>
          a.type === AbsenceType.SHORT &&
          a.reason?.code &&
          types.includes(a.reason?.code) &&
          isDateWithinRange(
            moment(a.startDate).toDate(),
            countRange.startDate,
            countRange.endDate,
          ),
      );
      return sickLeaveEntries.length >= limit;
    },
    [sickLeaves, otherAbsenceEntries, resolveSickLeaveCountRange],
  );

  const isHoursFieldEnabled = useCallback(
    (startDate?: Date | null, endDate?: Date | null) => {
      return (
        absenceHoursEnabled &&
        !!startDate &&
        !!endDate &&
        isSameDay(startDate, endDate)
      );
    },
    [absenceHoursEnabled],
  );

  return {
    selectedAbsenceType,
    categoryTabs: {
      enabled: !(selectedAbsence?.type ?? defaultAbsenceType),
      selected: selectedAbsenceType,
      onTabChange: handleAbsenceTypeSelect,
    },
    formProps: {
      employee: {
        legalEntityId,
      },
      absenceEntries,
      reasonOptions,
      selectedAbsence,
      selectedAbsenceType,
      companyVacationTypes,
      isHoursFieldEnabled,
      permissions: {
        canEditDays: !isOwnAbsence,
      },
      overlappingAbsence: {
        enabled: overlappingAbsencesAllowed,
        absenceTypes: overlappingAbsencesTypes,
        isOtherAbsenceDate: isOtherAbsenceDate,
        isOverlapAllowedOtherAbsenceDate,
        hasAbsenceOverlap,
      },
      sickLeaves: {
        isSickLeave,
        isSickLeaveCountSpent,
        getNextSickLeaveCountResetDate,
        getSickLeaveTypeDayLimit,
      },
      balances: {
        availableBalances,
        getAvailableBalance,
        isBalancePoolReasonMatch,
      },
      refreshAbsencesData,
      isLoadingData: isLoading,
      failedToLoadData: failedToLoad,
    },
    isLoading,
    failedToLoad,
  };
};
